import React, { useState } from 'react';
import { getDisplayName } from './utils/nameUtils';
import MemberModal from './components/MemberModal';
import './TeamDashboard.css';

const TEAM_ROLE_ID = process.env.REACT_APP_TEAM_ROLE_ID;

function TeamDashboard({ guildMembers, membersLoading, serverName }) {
    const [selectedMember, setSelectedMember] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const getTeamMembers = () => {
        return guildMembers.filter(member => member.roles && member.roles.includes(TEAM_ROLE_ID));
    };

    const teamMembers = getTeamMembers();
    const inVoice = teamMembers.filter(m => m.in_voice);
    const onlineNotVoice = teamMembers.filter(m => m.status === 'online' && !m.in_voice);
    const awayNotVoice = teamMembers.filter(m => (m.status === 'away' || m.status === 'idle') && !m.in_voice);
    const dndNotVoice = teamMembers.filter(m => m.status === 'dnd' && !m.in_voice);

    const offline = teamMembers.filter(m =>
        (m.status === 'offline' || !m.status || m.status === 'invisible') && !m.in_voice
    );

    const openMemberModal = (member) => {
        setSelectedMember(member);
        setIsModalOpen(true);
    };

    const closeMemberModal = () => {
        setSelectedMember(null);
        setIsModalOpen(false);
    };

    const renderMemberBlocks = (members, type) => (
        <div className="teamdashboard-member-grid">
            {members.map(member => {
                const statusClass = member.in_voice && type === 'voice' ? 'voice' : member.status;

                return (
                    <div
                        key={member.id}
                        className={`teamdashboard-member-block ${statusClass}`}
                        onClick={() => openMemberModal(member)}
                    >
                        <div className="teamdashboard-member-status"></div>
                        <span className="teamdashboard-member-name">{getDisplayName(member)}</span>
                        {(member.in_voice && type === 'voice') && (
                            <span className="teamdashboard-member-voice">
                                🎧 {member.voice_channel_name || 'In Voice'}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="teamdashboard-container">
            {/* Hero Section */}
            <div className="teamdashboard-hero">
                <div className="teamdashboard-hero-content">
                    <h2 className="teamdashboard-hero-title">Team Übersicht - {serverName}</h2>
                    <p className="teamdashboard-hero-subtitle">
                        Alle Mitglieder mit Team Rolle auf {serverName}
                    </p>
                </div>
                <div className="teamdashboard-hero-stats">
                    <span className="teamdashboard-hero-label">Team Mitglieder</span>
                    <strong className="teamdashboard-hero-value">{teamMembers.length}</strong>
                </div>
            </div>

            {/* Main Card */}
            <div className="teamdashboard-main-card">
                <div className="teamdashboard-card-header">
                    <h4 className="teamdashboard-card-title">Aktive Team Mitglieder</h4>
                    <div className="teamdashboard-status-pills">
                        <span className="teamdashboard-status-pill">
                            <span className="online">●</span> {onlineNotVoice.length}
                        </span>
                        <span className="teamdashboard-status-pill">
                            <span className="away">●</span> {awayNotVoice.length}
                        </span>
                        <span className="teamdashboard-status-pill">
                            <span className="dnd">●</span> {dndNotVoice.length}
                        </span>
                    </div>
                </div>

                {membersLoading ? (
                    <div className="teamdashboard-loading">Lädt Teamdaten...</div>
                ) : (
                    <div className="teamdashboard-status-grid">
                        {/* In Voice Card */}
                        <div className="teamdashboard-status-card voice">
                            <div className="teamdashboard-status-card-header">
                                <div className="teamdashboard-status-indicator" style={{ backgroundColor: '#4ADE80' }}></div>
                                <h5 className="teamdashboard-status-title">In Voice ({inVoice.length})</h5>
                            </div>
                            {inVoice.length > 0 ? renderMemberBlocks(inVoice, 'voice') : (
                                <div className="teamdashboard-empty">Niemand in Voice-Channels.</div>
                            )}
                        </div>

                        {/* Online Card */}
                        <div className="teamdashboard-status-card online">
                            <div className="teamdashboard-status-card-header">
                                <div className="teamdashboard-status-indicator" style={{ backgroundColor: '#60A5FA' }}></div>
                                <h5 className="teamdashboard-status-title">Online ({onlineNotVoice.length})</h5>
                            </div>
                            {onlineNotVoice.length > 0 ? renderMemberBlocks(onlineNotVoice, 'online') : (
                                <div className="teamdashboard-empty">Niemand online.</div>
                            )}
                        </div>

                        {/* Away Card */}
                        <div className="teamdashboard-status-card away">
                            <div className="teamdashboard-status-card-header">
                                <div className="teamdashboard-status-indicator" style={{ backgroundColor: '#FBBF24' }}></div>
                                <h5 className="teamdashboard-status-title">Abwesend ({awayNotVoice.length})</h5>
                            </div>
                            {awayNotVoice.length > 0 ? renderMemberBlocks(awayNotVoice, 'away') : (
                                <div className="teamdashboard-empty">Niemand abwesend.</div>
                            )}
                        </div>

                        {/* DND Card */}
                        <div className="teamdashboard-status-card dnd">
                            <div className="teamdashboard-status-card-header">
                                <div className="teamdashboard-status-indicator" style={{ backgroundColor: '#F87171' }}></div>
                                <h5 className="teamdashboard-status-title">Beschäftigt ({dndNotVoice.length})</h5>
                            </div>
                            {dndNotVoice.length > 0 ? renderMemberBlocks(dndNotVoice, 'dnd') : (
                                <div className="teamdashboard-empty">Niemand beschäftigt.</div>
                            )}
                        </div>

                        {/* Offline Card */}
                        <div className="teamdashboard-status-card offline">
                            <div className="teamdashboard-status-card-header">
                                <div className="teamdashboard-status-indicator" style={{ backgroundColor: '#6B7280' }}></div>
                                <h5 className="teamdashboard-status-title">Offline ({offline.length})</h5>
                            </div>
                            {offline.length > 0 ? renderMemberBlocks(offline, 'offline') : (
                                <div className="teamdashboard-empty">Alle Teamler sind online!</div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Member Modal */}
            {selectedMember && (
                <MemberModal
                    member={selectedMember}
                    isOpen={isModalOpen}
                    onClose={closeMemberModal}
                />
            )}
        </div>
    );
}

export default TeamDashboard;
